import { useEffect, useMemo, useState } from "react";
import type { Project } from "../../../../../../../../../api/useProjects";
import { useFormattedDatasetPeriodRange } from "../../../../../../hook/useFormattedDatasetPeriodRange";
import type { BlockPlotModelT } from "../../../../../../schema/block/plot";
import { defineOptions } from "../utils/options";
import { usePreset } from "./usePreset";
import { useCycleDisplay } from "./useCycleDisplay";
import { usePlotPeriod } from "./usePlotPeriod";
import {
  convertPresetToPeriod,
  getDateRangeNavigationPresets,
} from "../../../../../../component/navigation";
import { useWithResetZoomOptions } from "../ResetZoomButton";
import { useWithHiddenSeries } from "./useHiddenSeries";
import type { Dataset } from "../../../../../../hook/useDataset";
import useDistinctShape from "@ui/lib/hooks/useDistinctShape";
import type { useAvailableResources } from "../../../../../../hook/useAvailableResources";

export const usePlot = (
  project: Project,
  block: BlockPlotModelT,
  availableResources: ReturnType<typeof useAvailableResources>,
  dataset: Dataset,
  lastCycle?: { from: number; to: number },
) => {
  const stableBlock = useDistinctShape(block);
  const formattedPeriodRange = useFormattedDatasetPeriodRange(dataset.periodRange);
  const preset = usePreset(stableBlock);
  const [displayCycle] = useCycleDisplay({
    block: stableBlock,
    chart: { current: null },
    scope: stableBlock.id,
  });

  const optionDatasetInput = useMemo(
    () => [...dataset.quantities, ...dataset.faults],
    [dataset.faults, dataset.quantities],
  );
  const [optionConfig, setOptionConfig] = useState(stableBlock.config);

  const [selectedPeriod] = usePlotPeriod(stableBlock);
  const presets = useMemo(
    () =>
      getDateRangeNavigationPresets({
        from: dataset.periodRange.from,
        to: dataset.periodRange.to,
        cycle: lastCycle,
        timezone: project.timezone,
      }),
    [dataset.periodRange.from, dataset.periodRange.to, lastCycle, project.timezone],
  );
  const initialTemporalZoom = useMemo(() => {
    if (!stableBlock.config.options || !stableBlock.config.options.temporalZoom)
      return { from: dataset.periodRange.from, to: dataset.periodRange.to };

    if (
      selectedPeriod &&
      typeof selectedPeriod !== "string" &&
      selectedPeriod.from &&
      selectedPeriod.to
    ) {
      return {
        from: new Date(selectedPeriod.from).getTime(),
        to: new Date(selectedPeriod.to).getTime(),
      };
    }

    const presetValue =
      presets.find(
        preset => preset.metadata?.["id"] === stableBlock.config.options?.temporalZoom,
      ) ?? null;
    const presetPeriod = presetValue ? convertPresetToPeriod(presetValue, project.timezone) : null;

    return presetPeriod ?? { from: dataset.periodRange.from, to: dataset.periodRange.to };
  }, [
    stableBlock.config.options,
    dataset.periodRange.from,
    dataset.periodRange.to,
    selectedPeriod,
    presets,
    project.timezone,
  ]);

  useEffect(() => {
    if (JSON.stringify(optionConfig) !== JSON.stringify(stableBlock.config)) {
      setOptionConfig(stableBlock.config);
    }
  }, [stableBlock.config, optionConfig]);

  const options = useMemo(() => {
    if (optionConfig.layers.length > 0) {
      const options = defineOptions(
        stableBlock.id,
        { ...optionConfig, options: { ...(optionConfig.options ?? {}), displayCycle } },
        optionDatasetInput,
        availableResources,
        initialTemporalZoom,
      );
      return options;
    }
    return null;
  }, [
    optionConfig,
    stableBlock.id,
    displayCycle,
    optionDatasetInput,
    availableResources,
    initialTemporalZoom,
  ]);

  const optionsWithZoomReset = useWithResetZoomOptions(stableBlock, options);
  const optionsWithHiddenSeries = useWithHiddenSeries(stableBlock, optionsWithZoomReset);

  return useMemo(
    () => ({ dataset, formattedPeriodRange, options: optionsWithHiddenSeries, preset }),
    [dataset, formattedPeriodRange, optionsWithHiddenSeries, preset],
  );
};
